import React, { useId } from 'react';
import { UniversalLink } from '@plone/volto/components';
import Image from '@plone/volto/components/theme/Image/Image';
import type { HeadingLevel } from 'volto-ministerio-publico-federal/types/heading';

export type CampaignCardImage =
  | { src: string; item?: never }
  | { item: unknown; src?: never };

export interface CampaignCardData {
  key: string;
  href: string;
  title: string;
  description?: string;
  image: CampaignCardImage | null;
  alt: string;
  opensInNewTab: boolean;
}

export interface CampaignCardProps {
  campaign: CampaignCardData;
  as?: HeadingLevel;
}

/**
 * Só o título é link. O realce de hover/foco cobre o card inteiro por CSS
 * (`:focus-within`), sem alargar a área clicável nem o nome acessível.
 */
export const CampaignCard: React.FC<CampaignCardProps> = React.memo(
  function CampaignCard({ campaign, as: Heading = 'h3' }) {
    const titleId = useId();

    return (
      <article
        aria-labelledby={titleId}
        className={`mpf-campaign-card${
          campaign.image ? '' : ' mpf-campaign-card--no-image'
        }`}
      >
        {campaign.image && (
          <Image
            {...campaign.image}
            alt={campaign.alt}
            loading="lazy"
            className="mpf-campaign-card__image"
          />
        )}

        <div className="mpf-campaign-card__content">
          <Heading id={titleId} className="mpf-campaign-card__title">
            <UniversalLink
              href={campaign.href}
              openLinkInNewTab={campaign.opensInNewTab}
            >
              {campaign.title}
              {campaign.opensInNewTab && (
                <span className="sr-only"> (abre em nova aba)</span>
              )}
            </UniversalLink>
          </Heading>

          {campaign.description && (
            <p className="mpf-campaign-card__description">
              {campaign.description}
            </p>
          )}
        </div>
      </article>
    );
  },
);

export default CampaignCard;
